import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusCircle, Edit, Trash2, Loader2, AlertCircle, Info, Clock,
    Sliders, Briefcase, Moon, X, ShieldAlert, Search, Sparkles,
    Coffee, CheckCircle2, Activity, CalendarRange
} from 'lucide-react';

const mapShiftType = (val) => {
    if (val === 0 || val === '0') return 'Standard';
    if (val === 1 || val === '1') return 'FlexibleWindow';
    if (val === 2 || val === '2') return 'FlexibleDaily';
    if (val === 3 || val === '3') return 'FlexibleWeekly';
    return val || 'Standard';
};

const mapCalculationMethod = (val) => {
    if (val === 0 || val === '0') return 'BasedOnFirstEntryLastExit';
    if (val === 1 || val === '1') return 'BasedOnTimeSpentInside';
    return val || 'BasedOnFirstEntryLastExit';
};

const mapDayOfWeek = (val) => {
    const map = {
        'Sunday': '0', 0: '0', '0': '0',
        'Monday': '1', 1: '1', '1': '1',
        'Tuesday': '2', 2: '2', '2': '2',
        'Wednesday': '3', 3: '3', '3': '3',
        'Thursday': '4', 4: '4', '4': '4',
        'Friday': '5', 5: '5', '5': '5',
        'Saturday': '6', 6: '6', '6': '6'
    };
    return map[val] !== undefined ? map[val] : '1';
};

const isNightShiftHelper = (shift) => {
    if (!shift) return false;
    const type = mapShiftType(shift.shiftType);
    if (type === 'FlexibleDaily' || type === 'FlexibleWeekly') return false;
    if (shift.isNextDayShift) return true;
    if (!shift.startTime || !shift.endTime) return false;
    return shift.endTime.substring(0, 5) <= shift.startTime.substring(0, 5);
};

// ====================================================================
//     Vardiya Formu Bileşeni (Sekmeli ve Canlı Önizlemeli Modal)
// ====================================================================
const WorkShiftForm = ({ currentShift, onSave, onCancel, permissions }) => {
    const getInitialFormData = () => {
        const defaults = {
            name: '',
            shortCode: '',
            startTime: '09:00:00',
            endTime: '18:00:00',
            calculationMethod: "BasedOnFirstEntryLastExit",
            requiredDurationMinutes: null,
            isNextDayShift: false,
            entryToleranceMinutes: 5,
            exitToleranceMinutes: 5,
            earlyOvertimeEnabled: false,
            earlyOvertimeThresholdMinutes: 30,
            lateOvertimeEnabled: true,
            lateOvertimeThresholdMinutes: 30,
            shiftCatchmentStartHours: 2,
            shiftCatchmentEndHours: 2,
            maxOvertimeMinutes: 120,
            minValidWorkMinutes: 60,
            tolerateLateEntryInActualDuration: true,
            tolerateEarlyExitInActualDuration: true,
            isPreviousDayShift: false,
            recordOnShiftStartDate: true,
            breakIds: [],
            shiftType: 'Standard',
            flexibleEntryStartTime: '07:30',
            flexibleEntryEndTime: '09:00',
            limitEarlyEntryToWindow: true,
            weeklyFlexStartDay: '1', // 1=Monday
            convertRemainingDaysToHoliday: false,
            giveOvertimeOnRemainingDays: false
        };
        if (!currentShift) return defaults;

        const result = {
            ...defaults,
            ...currentShift,
            shiftType: mapShiftType(currentShift.shiftType),
            calculationMethod: mapCalculationMethod(currentShift.calculationMethod),
            weeklyFlexStartDay: mapDayOfWeek(currentShift.weeklyFlexStartDay),
            startTime: currentShift.startTime ? currentShift.startTime.substring(0, 8) : '09:00:00',
            endTime: currentShift.endTime ? currentShift.endTime.substring(0, 8) : '18:00:00',
            flexibleEntryStartTime: currentShift.flexibleEntryStartTime ? currentShift.flexibleEntryStartTime.substring(0, 5) : '07:30',
            flexibleEntryEndTime: currentShift.flexibleEntryEndTime ? currentShift.flexibleEntryEndTime.substring(0, 5) : '09:00',
        };

        if (result.shiftType === 'FlexibleWindow' && result.limitEarlyEntryToWindow) {
            result.earlyOvertimeEnabled = false;
        }

        if (result.shiftType === 'FlexibleDaily' || result.shiftType === 'FlexibleWeekly') {
            result.shiftCatchmentStartHours = 0;
            result.earlyOvertimeEnabled = false;
        }

        if (result.shiftType !== 'FlexibleWeekly') {
            result.requiredDurationMinutes = null;
        } else if (!result.requiredDurationMinutes || result.requiredDurationMinutes <= 600) {
            result.requiredDurationMinutes = 2700;
        }

        return result;
    };

    const [formData, setFormData] = useState(getInitialFormData());
    const [availableBreaks, setAvailableBreaks] = useState([]);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [durationUnit, setDurationUnit] = useState('minutes');
    const [activeSection, setActiveSection] = useState('general'); // general | breaks | rules

    const isDurationBasedFlex = formData.shiftType === 'FlexibleDaily' || formData.shiftType === 'FlexibleWeekly';

    useEffect(() => {
        const fetchBreaks = async () => {
            try {
                const data = await apiClient('scheduling/breaks', { method: 'GET' });
                setAvailableBreaks(data || []);
            } catch {
                setError('Molalar yüklenemedi.');
            }
        };
        fetchBreaks();
    }, []);

    // Brüt ve dinamik net süreyi üstte seçilen saatlerden ve ücretsiz molalardan hesapla
    const calculateGrossMinutes = () => {
        if (!formData.startTime || !formData.endTime) return 0;

        const parseTime = (t) => {
            const parts = t.split(':');
            const h = parseInt(parts[0], 10) || 0;
            const m = parseInt(parts[1], 10) || 0;
            return h * 60 + m;
        };

        let startMins = parseTime(formData.startTime);
        let endMins = parseTime(formData.endTime);

        if (endMins <= startMins) {
            endMins += 24 * 60;
        }

        return Math.max(0, endMins - startMins);
    };

    const grossMinutes = calculateGrossMinutes();
    const selectedUnpaidBreaksMinutes = availableBreaks
        .filter(b => formData.breakIds.includes(b.id) && !b.isPaid)
        .reduce((sum, b) => sum + b.durationMinutes, 0);
    const autoCalculatedMinutes = Math.max(0, grossMinutes - selectedUnpaidBreaksMinutes);

    useEffect(() => {
        setFormData(getInitialFormData());
    }, [currentShift]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            let parsedValue = value;
            if (type === 'number') {
                parsedValue = value === '' ? null : parseFloat(value);
                if (isNaN(parsedValue)) {
                    parsedValue = null;
                }
            } else if (type === 'checkbox') {
                parsedValue = checked;
            }

            const newData = { ...prev, [name]: parsedValue };

            if (name === 'limitEarlyEntryToWindow' && parsedValue === true && prev.shiftType === 'FlexibleWindow') {
                newData.earlyOvertimeEnabled = false;
            }
            if (name === 'shiftType' && parsedValue === 'FlexibleWindow' && prev.limitEarlyEntryToWindow === true) {
                newData.earlyOvertimeEnabled = false;
            }

            if (name === 'shiftType' && (parsedValue === 'FlexibleDaily' || parsedValue === 'FlexibleWeekly')) {
                newData.shiftCatchmentStartHours = 0;
                newData.earlyOvertimeEnabled = false;
            }

            return newData;
        });
    };

    const handleShiftTypeSelect = (newType) => {
        setFormData(prev => {
            const next = { ...prev, shiftType: newType };
            if (newType === 'FlexibleWindow' && prev.limitEarlyEntryToWindow) {
                next.earlyOvertimeEnabled = false;
            }
            if (newType === 'FlexibleDaily') {
                next.shiftCatchmentStartHours = 0;
                next.earlyOvertimeEnabled = false;
                next.requiredDurationMinutes = null;
            } else if (newType === 'FlexibleWeekly') {
                next.shiftCatchmentStartHours = 0;
                next.earlyOvertimeEnabled = false;
                if (!next.requiredDurationMinutes || next.requiredDurationMinutes <= 600) {
                    next.requiredDurationMinutes = 2700;
                }
            } else {
                next.requiredDurationMinutes = null;
            }
            return next;
        });
    };

    const handleBreakChange = (breakId) => {
        setFormData(prev => {
            const newBreakIds = prev.breakIds.includes(breakId)
                ? prev.breakIds.filter(id => id !== breakId)
                : [...prev.breakIds, breakId];
            return { ...prev, breakIds: newBreakIds };
        });
    };

    const formatTimeForApi = (t) => {
        if (!t) return null;
        return t.length === 5 ? `${t}:00` : t.substring(0, 8);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isNew = !formData.id;
        const requiredPermission = isNew ? 'scheduling:create' : 'scheduling:update';
        if (!permissions.includes(requiredPermission)) {
            setError(`Bu işlemi yapma yetkiniz yok.`);
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            const method = isNew ? 'POST' : 'PUT';
            const url = isNew ? `scheduling/shifts` : `scheduling/shifts/${formData.id}`;

            const resolvedRequiredMinutes = formData.shiftType === 'FlexibleWeekly'
                ? (formData.requiredDurationMinutes || 2700)
                : (formData.shiftType === 'FlexibleDaily' ? autoCalculatedMinutes : null);

            const payload = {
                ...formData,
                calculationMethod: formData.calculationMethod,
                shiftType: formData.shiftType,
                startTime: formatTimeForApi(formData.startTime) || '09:00:00',
                endTime: formatTimeForApi(formData.endTime) || '18:00:00',
                requiredDurationMinutes: resolvedRequiredMinutes,
                flexibleEntryStartTime: formData.shiftType === 'FlexibleWindow' ? formatTimeForApi(formData.flexibleEntryStartTime) : null,
                flexibleEntryEndTime: formData.shiftType === 'FlexibleWindow' ? formatTimeForApi(formData.flexibleEntryEndTime) : null,
                weeklyFlexStartDay: formData.shiftType === 'FlexibleWeekly' ? parseInt(formData.weeklyFlexStartDay, 10) : null,
                convertRemainingDaysToHoliday: formData.shiftType === 'FlexibleWeekly' ? formData.convertRemainingDaysToHoliday : false,
                giveOvertimeOnRemainingDays: formData.shiftType === 'FlexibleWeekly' ? formData.giveOvertimeOnRemainingDays : false,
                earlyOvertimeEnabled: ((formData.shiftType === 'FlexibleWindow' && formData.limitEarlyEntryToWindow) || isDurationBasedFlex) ? false : formData.earlyOvertimeEnabled,
                shiftCatchmentStartHours: isDurationBasedFlex ? 0 : formData.shiftCatchmentStartHours,
            };

            await apiClient(url, { method, body: payload });
            onSave();
        } catch (err) {
            setError(err.message || 'Bir hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const effectiveNetMinutes = formData.shiftType === 'FlexibleWeekly'
        ? (formData.requiredDurationMinutes || 2700)
        : autoCalculatedMinutes;

    const shiftTypeCards = [
        {
            id: 'Standard',
            title: 'Sabit Vardiya',
            desc: 'Sabit giriş ve çıkış saatleri (Örn: 08:00 - 17:00)',
            badge: 'Standart'
        },
        {
            id: 'FlexibleWindow',
            title: 'Pencereli Esnek',
            desc: 'Belirli aralıkta (Örn: 07:30 - 09:30) esnek giriş',
            badge: 'Sliding Window'
        },
        {
            id: 'FlexibleDaily',
            title: 'Günlük Esnek',
            desc: 'Saat bağımsız günlük toplam süre hedefi (Örn: 8 sa)',
            badge: 'Daily Flex'
        },
        {
            id: 'FlexibleWeekly',
            title: 'Haftalık Esnek',
            desc: 'Haftalık kümülatif hedef süre takibi (Örn: 45 sa)',
            badge: 'Weekly Flex'
        }
    ];

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-700 max-h-[92vh] flex flex-col overflow-hidden">
                {/* Üst Sabit Başlık + Canlı Özet Şeridi */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white px-6 py-4 border-b border-indigo-500/20">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-indigo-500/20 border border-indigo-400/30 text-indigo-300">
                                <Briefcase size={22} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">
                                    {formData.id ? `Vardiya Düzenle: ${formData.name}` : 'Yeni Vardiya Kuralı Tanımla'}
                                </h3>
                                <p className="text-xs text-indigo-200/80">
                                    Vardiya çalışma saatlerini, esneklik modelini, molaları ve fazla mesai kurallarını yapılandırın.
                                </p>
                            </div>
                        </div>
                        <button onClick={onCancel} className="p-1.5 rounded-lg text-indigo-200 hover:text-white hover:bg-white/10">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Canlı Hesaplama Özeti Şeridi */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4 pt-3 border-t border-white/10 text-xs">
                        <div className="bg-white/10 rounded-lg px-3 py-2">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Esneklik Modeli</span>
                            <span className="font-bold text-white">
                                {shiftTypeCards.find(c => c.id === formData.shiftType)?.title}
                            </span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-2">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">
                                {formData.shiftType === 'FlexibleWeekly' ? 'Haftalık Net Hedef' : 'Günlük Net Hedef'}
                            </span>
                            <span className="font-bold text-emerald-300">
                                {effectiveNetMinutes} dk ({(effectiveNetMinutes / 60).toFixed(1)} saat)
                            </span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-2">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Seçili Molalar</span>
                            <span className="font-bold text-amber-300">
                                {formData.breakIds.length} Mola (-{selectedUnpaidBreaksMinutes} dk ücretsiz)
                            </span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-2">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Tolerans & Fazla Mesai</span>
                            <span className="font-bold text-purple-200">
                                {!isDurationBasedFlex ? `G:${formData.entryToleranceMinutes}dk / Ç:${formData.exitToleranceMinutes}dk` : `Eksik Tol: ${formData.exitToleranceMinutes}dk`}
                                {' • '}
                                {formData.lateOvertimeEnabled ? `FM >${formData.lateOvertimeThresholdMinutes}dk` : 'FM Kapalı'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Modal İçi Sekme Navigasyonu */}
                <div className="bg-gray-50 dark:bg-gray-900/60 px-6 py-2.5 border-b border-gray-200 dark:border-gray-700 flex gap-2 overflow-x-auto">
                    {[
                        { id: 'general', label: '1. Temel Bilgiler & Esneklik Modeli', icon: Briefcase },
                        { id: 'breaks', label: `2. Molalar (${formData.breakIds.length}) & Gece Döngüsü`, icon: Coffee },
                        { id: 'rules', label: '3. Tolerans, Sapma & Fazla Mesai Kuralları', icon: Sliders }
                    ].map(sec => {
                        const Icon = sec.icon;
                        const active = activeSection === sec.id;
                        return (
                            <button
                                key={sec.id}
                                type="button"
                                onClick={() => setActiveSection(sec.id)}
                                className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all whitespace-nowrap ${
                                    active
                                        ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200/70 dark:hover:bg-gray-800'
                                }`}
                            >
                                <Icon size={15} />
                                {sec.label}
                            </button>
                        );
                    })}
                </div>

                {/* Form Gövdesi */}
                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
                    {error && (
                        <div className="text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 p-3.5 rounded-xl border border-red-200 dark:border-red-800 flex items-center text-sm">
                            <AlertCircle size={18} className="mr-2 flex-shrink-0" /> {error}
                        </div>
                    )}

                    {/* SEKME 1: TEMEL BİLGİLER & ESNEKLİK MODELİ */}
                    {activeSection === 'general' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Vardiya Adı</label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="Örn: 09:00 - 18:00 Merkez Ofis Vardiyası"
                                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="shortCode" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Kısa Kod (Rapor Kodu)</label>
                                    <input
                                        type="text"
                                        name="shortCode"
                                        id="shortCode"
                                        value={formData.shortCode}
                                        onChange={handleChange}
                                        placeholder="Örn: OFS, GN, GCE"
                                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono uppercase focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            {/* 4 Vardiya Türü Seçim Kartları */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                    Vardiya Çalışma ve Esneklik Türü
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {shiftTypeCards.map(card => {
                                        const selected = formData.shiftType === card.id;
                                        return (
                                            <button
                                                key={card.id}
                                                type="button"
                                                onClick={() => handleShiftTypeSelect(card.id)}
                                                className={`p-3.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                                                    selected
                                                        ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20'
                                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                                }`}
                                            >
                                                <div>
                                                    <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mb-1.5 ${
                                                        selected ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                                                    }`}>
                                                        {card.badge}
                                                    </span>
                                                    <div className="font-bold text-sm text-gray-900 dark:text-white">{card.title}</div>
                                                </div>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-snug">{card.desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Puantaj Hesaplama Yöntemi */}
                            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                    Puantaj Süre Hesaplama Yöntemi
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, calculationMethod: 'BasedOnFirstEntryLastExit' }))}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            formData.calculationMethod === 'BasedOnFirstEntryLastExit'
                                                ? 'border-indigo-600 bg-white dark:bg-gray-800 ring-2 ring-indigo-500/20'
                                                : 'border-gray-200 dark:border-gray-700 opacity-75 hover:opacity-100'
                                        }`}
                                    >
                                        <div className="font-bold text-sm text-gray-900 dark:text-white">İlk Giriş – Son Çıkış Arası</div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Günün ilk girişi ile son çıkışı arasındaki brüt süreden mola kuralları düşülür.
                                        </p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, calculationMethod: 'BasedOnTimeSpentInside' }))}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            formData.calculationMethod === 'BasedOnTimeSpentInside'
                                                ? 'border-indigo-600 bg-white dark:bg-gray-800 ring-2 ring-indigo-500/20'
                                                : 'border-gray-200 dark:border-gray-700 opacity-75 hover:opacity-100'
                                        }`}
                                    >
                                        <div className="font-bold text-sm text-gray-900 dark:text-white">İçeride Geçirilen Toplam Süre</div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Gün içindeki tüm Giriş-Çıkış çiftleri toplanır; dışarıda geçen boşluklar molalarla eşleştirilir.
                                        </p>
                                    </button>
                                </div>
                            </div>

                            {/* Saat Aralığı (Tüm vardiya modellerinde günlük hedef / referans barem saatleri için) */}
                            <div className="p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 space-y-4">
                                {formData.shiftType !== 'Standard' && (
                                    <div className="flex items-start gap-2 text-xs text-blue-800 dark:text-blue-200 bg-blue-100/70 dark:bg-blue-900/40 p-3 rounded-lg">
                                        <Info size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
                                        <span>
                                            {formData.shiftType === 'FlexibleWindow' && (
                                                <>
                                                    <strong>Varsayılan (İdeal) Saatler:</strong> Günlük zorunlu net çalışma süresi, aşağıda seçtiğiniz saat aralığından ücretsiz molalar düşülerek <strong>otomatik</strong> hesaplanır. Personel esnek pencerede hangi saatte girerse girsin bu günlük net süreyi tamamlamakla yükümlüdür.
                                                </>
                                            )}
                                            {formData.shiftType === 'FlexibleDaily' && (
                                                <>
                                                    <strong>Referans (Barem) Saatler:</strong> Günlük Esnek Mesaide sabit giriş-çıkış saati cezası yoktur; ancak <strong>Günlük Zorunlu Net Çalışma Süresi</strong> ve izin/devamsızlık baremi, aşağıda seçtiğiniz saat aralığından ücretsiz molalar düşülerek <strong>otomatik</strong> hesaplanır.
                                                </>
                                            )}
                                            {formData.shiftType === 'FlexibleWeekly' && (
                                                <>
                                                    <strong>Günlük Referans Saatler:</strong> Haftalık Esnek Mesaide tek bir iş gününün referans mesai süresini ({autoCalculatedMinutes} dk / {(autoCalculatedMinutes / 60).toFixed(1)} saat) ve tam gün izin/tatil baremini belirler.
                                                </>
                                            )}
                                        </span>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="startTime" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                            {formData.shiftType === 'Standard' ? 'Vardiya Başlangıç Saati' : 'Referans / Varsayılan Başlangıç Saati'}
                                        </label>
                                        <input
                                            type="time"
                                            name="startTime"
                                            id="startTime"
                                            value={formData.startTime}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="endTime" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                            {formData.shiftType === 'Standard' ? 'Vardiya Bitiş Saati' : 'Referans / Varsayılan Bitiş Saati'}
                                        </label>
                                        <input
                                            type="time"
                                            name="endTime"
                                            id="endTime"
                                            value={formData.endTime}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Pencereli Esnek Mesai Ayarları */}
                            {formData.shiftType === 'FlexibleWindow' && (
                                <div className="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 space-y-4">
                                    <h4 className="text-sm font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-2">
                                        <Clock size={16} /> Esnek Giriş Penceresi Ayarları (Sliding Window)
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="flexibleEntryStartTime" className="block text-xs font-bold uppercase text-indigo-900 dark:text-indigo-200 mb-1">
                                                En Erken Giriş Saati
                                            </label>
                                            <input
                                                type="time"
                                                name="flexibleEntryStartTime"
                                                id="flexibleEntryStartTime"
                                                value={formData.flexibleEntryStartTime}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3.5 py-2 border border-indigo-300 dark:border-indigo-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="flexibleEntryEndTime" className="block text-xs font-bold uppercase text-indigo-900 dark:text-indigo-200 mb-1">
                                                En Geç Giriş Saati (Tavan Sınır)
                                            </label>
                                            <input
                                                type="time"
                                                name="flexibleEntryEndTime"
                                                id="flexibleEntryEndTime"
                                                value={formData.flexibleEntryEndTime}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-3.5 py-2 border border-indigo-300 dark:border-indigo-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />
                                        </div>
                                    </div>
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            id="limitEarlyEntryToWindow"
                                            name="limitEarlyEntryToWindow"
                                            checked={formData.limitEarlyEntryToWindow}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                        <div className="text-xs text-indigo-900 dark:text-indigo-200">
                                            <strong>Erken Girişi Pencere Başlangıcına Kırp (Snap):</strong> Personel {formData.flexibleEntryStartTime} saatinden önce gelse bile mesaisi {formData.flexibleEntryStartTime}'da başlamış sayılır.
                                        </div>
                                    </label>
                                </div>
                            )}

                            {/* Haftalık Esnek Ayarları */}
                            {formData.shiftType === 'FlexibleWeekly' && (
                                <div className="p-4 rounded-xl bg-purple-50/70 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 space-y-4">
                                    <h4 className="text-sm font-bold text-purple-800 dark:text-purple-300 flex items-center gap-2">
                                        <CalendarRange size={16} /> Haftalık Esnek Mesai Kuralları
                                    </h4>
                                    <div>
                                        <label htmlFor="weeklyFlexStartDay" className="block text-xs font-bold uppercase text-purple-900 dark:text-purple-200 mb-1">
                                            Haftalık Kümülatif Hesap Başlangıç Günü
                                        </label>
                                        <select
                                            name="weeklyFlexStartDay"
                                            value={formData.weeklyFlexStartDay}
                                            onChange={handleChange}
                                            className="w-full px-3.5 py-2 border border-purple-300 dark:border-purple-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            <option value="1">Pazartesi</option>
                                            <option value="2">Salı</option>
                                            <option value="3">Çarşamba</option>
                                            <option value="4">Perşembe</option>
                                            <option value="5">Cuma</option>
                                            <option value="6">Cumartesi</option>
                                            <option value="0">Pazar</option>
                                        </select>
                                    </div>
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            id="convertRemainingDaysToHoliday"
                                            name="convertRemainingDaysToHoliday"
                                            checked={formData.convertRemainingDaysToHoliday}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                        />
                                        <div className="text-xs text-purple-900 dark:text-purple-200">
                                            <strong>Hedef Süre Dolduktan Sonraki Kalan Günleri Otomatik "Hafta Tatili (HT)" Say:</strong> Örn. personel 45 saati Perşembe doldurursa Cuma-Cumartesi-Pazar otomatik HT olur.
                                        </div>
                                    </label>
                                    {formData.convertRemainingDaysToHoliday && (
                                        <label className="flex items-start gap-2.5 ml-6 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="giveOvertimeOnRemainingDays"
                                                name="giveOvertimeOnRemainingDays"
                                                checked={formData.giveOvertimeOnRemainingDays}
                                                onChange={handleChange}
                                                className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                            />
                                            <div className="text-xs text-purple-900 dark:text-purple-200">
                                                <strong>HT Sayılan Kalan Günlerde İşe Gelirse Hafta Tatili Fazla Mesaisi (HT FM) Ver</strong>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            )}

                            {/* Zorunlu Çalışma Süresi: Günlük Modellerde Otomatik, Haftalık Esnekte Haftalık Hedef */}
                            {formData.shiftType !== 'FlexibleWeekly' ? (
                                <div className="p-4 rounded-xl bg-emerald-50/70 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/80">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-bold text-emerald-900 dark:text-emerald-300">
                                                    Günlük Zorunlu Net Çalışma Süresi
                                                </span>
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-600 text-white">
                                                    Otomatik Hesaplanır
                                                </span>
                                            </div>
                                            <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300">
                                                Hesaplama motoru günlük bazda çalıştığından hata yapılmasını önlemek için üstteki başlangıç-bitiş saatlerinden ücretsiz molalar düşülerek otomatik hesaplanır.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2.5 rounded-xl border border-emerald-300 dark:border-emerald-700 shadow-sm flex-shrink-0">
                                            <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                                            <div>
                                                <div className="text-base font-extrabold text-emerald-700 dark:text-emerald-300 leading-tight">
                                                    {autoCalculatedMinutes} Dakika ({(autoCalculatedMinutes / 60).toFixed(1)} Saat)
                                                </div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    Brüt {grossMinutes} dk − {selectedUnpaidBreaksMinutes} dk ücretsiz mola
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 rounded-xl bg-emerald-50/60 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/80 space-y-3">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                        <label htmlFor="requiredDurationMinutes" className="block text-sm font-bold text-emerald-900 dark:text-emerald-300">
                                            Haftalık Zorunlu Çalışma Süresi (Zorunlu)
                                        </label>
                                        <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 bg-white/80 dark:bg-gray-800 px-2.5 py-1 rounded-lg border border-emerald-200 dark:border-emerald-700">
                                            Günlük Referans Net: {autoCalculatedMinutes} dk ({(autoCalculatedMinutes / 60).toFixed(1)} saat)
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            type="number"
                                            name="requiredDurationMinutes"
                                            id="requiredDurationMinutes"
                                            value={formData.requiredDurationMinutes !== null && formData.requiredDurationMinutes !== undefined
                                                ? (durationUnit === 'hours' ? formData.requiredDurationMinutes / 60 : formData.requiredDurationMinutes)
                                                : ''}
                                            onChange={(e) => {
                                                const val = parseFloat(e.target.value);
                                                let mins = isNaN(val) ? null : val;
                                                if (mins !== null && durationUnit === 'hours') {
                                                    mins = Math.round(mins * 60);
                                                }
                                                setFormData({ ...formData, requiredDurationMinutes: mins });
                                            }}
                                            min="1"
                                            step={durationUnit === 'hours' ? "0.5" : "1"}
                                            required
                                            className="w-40 px-3.5 py-2 border border-emerald-300 dark:border-emerald-700 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold"
                                        />
                                        <select
                                            value={durationUnit}
                                            onChange={(e) => setDurationUnit(e.target.value)}
                                            className="px-3 py-2 border border-emerald-300 dark:border-emerald-700 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-semibold"
                                        >
                                            <option value="minutes">Dakika</option>
                                            <option value="hours">Saat</option>
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, requiredDurationMinutes: autoCalculatedMinutes * 5 }))}
                                            className="px-3 py-2 text-xs font-bold rounded-xl bg-emerald-100 hover:bg-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 transition-colors"
                                        >
                                            5 Gün × Günlük Net ({autoCalculatedMinutes * 5} dk)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, requiredDurationMinutes: 2700 }))}
                                            className="px-3 py-2 text-xs font-bold rounded-xl bg-purple-100 hover:bg-purple-200 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 transition-colors"
                                        >
                                            Standart 45 Saat (2700 dk)
                                        </button>
                                    </div>
                                    <p className="text-xs text-emerald-800 dark:text-emerald-300">
                                        Haftalık Esnek Mesaide personelin bir hafta içinde tamamlaması gereken kümülatif net çalışma süresidir (Örn: 2700 dk / 45 saat).
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* SEKME 2: MOLALAR & GECE DÖNGÜSÜ */}
                    {activeSection === 'breaks' && (
                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white">Vardiyaya Dahil Edilecek Molalar</h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Bu vardiyada çalışan personellere uygulanacak mola kurallarını seçin.
                                        </p>
                                    </div>
                                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                                        {formData.breakIds.length} Mola Seçili
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {availableBreaks.map(breakItem => {
                                        const isSelected = formData.breakIds.includes(breakItem.id);
                                        return (
                                            <button
                                                key={breakItem.id}
                                                type="button"
                                                onClick={() => handleBreakChange(breakItem.id)}
                                                className={`p-3.5 rounded-xl border text-left transition-all flex items-center justify-between ${
                                                    isSelected
                                                        ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20'
                                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {}}
                                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                    />
                                                    <div>
                                                        <div className="font-bold text-sm text-gray-900 dark:text-white">{breakItem.name}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            {breakItem.windowStartTime && breakItem.windowEndTime
                                                                ? `${breakItem.windowStartTime.substring(0, 5)} - ${breakItem.windowEndTime.substring(0, 5)}`
                                                                : 'Serbest Zamanlı'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-xs font-bold text-gray-800 dark:text-gray-200 block">
                                                        {breakItem.durationMinutes} dk
                                                    </span>
                                                    <span className={`text-[10px] font-bold ${breakItem.isPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                        {breakItem.isPaid ? 'Ücretli' : 'Ücretsiz'}
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                    {availableBreaks.length === 0 && (
                                        <p className="text-sm text-gray-500 col-span-2 py-4 text-center">
                                            Henüz tanımlanmış mola bulunmuyor. Önce Mola Tanımları sekmesinden mola ekleyebilirsiniz.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {!isDurationBasedFlex && (
                                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 space-y-4">
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Moon size={16} className="text-indigo-500" />
                                        Gece Vardiyası ve Puantaj Tarih Atama Kuralları
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="recordOnShiftStartDate"
                                                name="recordOnShiftStartDate"
                                                checked={formData.recordOnShiftStartDate}
                                                onChange={handleChange}
                                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <div className="text-xs">
                                                <div className="font-bold text-gray-900 dark:text-white">Puantaj Kaydını Vardiya Başlangıç Gününe Yaz</div>
                                                <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Gece yarısını geçen vardiyalarda (Örn: Pazartesi 22:00 - Salı 06:00) mesaiyi başladığı günün (Pazartesi) puantajına yazar.
                                                </p>
                                            </div>
                                        </label>

                                        <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="isPreviousDayShift"
                                                checked={formData.isPreviousDayShift}
                                                onChange={(e) => setFormData({ ...formData, isPreviousDayShift: e.target.checked })}
                                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <div className="text-xs">
                                                <div className="font-bold text-gray-900 dark:text-white">Gece Yarısından Sonra Başlayan Vardiyayı Önceki Güne Bağla</div>
                                                <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Örn: 00:30 - 08:30 vardiyasını bir önceki takvim gününün üretim vardiyası olarak değerlendirir.
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* SEKME 3: TOLERANS, SAPMA & FAZLA MESAİ KURALLARI */}
                    {activeSection === 'rules' && (
                        <div className="space-y-6">
                            {isDurationBasedFlex && (
                                <div className="p-3.5 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 text-xs text-purple-900 dark:text-purple-200 flex items-start gap-2.5">
                                    <Info size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Süre Odaklı Esnek Mesai:</strong> Sabit bir giriş saati bulunmadığından <em>"Geç Giriş"</em> cezası uygulanmaz. Aşağıdaki <strong>Eksik Süre Toleransı</strong>, hedef süreden kaç dakikaya kadar eksik çalışmanın tolere edileceğini belirler.
                                    </span>
                                </div>
                            )}

                            {/* Toleranslar ve Min/Max Sınırlar */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {!isDurationBasedFlex && (
                                    <div>
                                        <label htmlFor="entryToleranceMinutes" className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                            Giriş Toleransı (dk)
                                        </label>
                                        <input
                                            type="number"
                                            name="entryToleranceMinutes"
                                            id="entryToleranceMinutes"
                                            value={formData.entryToleranceMinutes}
                                            onChange={handleChange}
                                            min="0"
                                            className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                        />
                                        <p className="mt-1 text-[11px] text-gray-500">Geç giriş sayılmayacak süre.</p>
                                    </div>
                                )}
                                <div>
                                    <label htmlFor="exitToleranceMinutes" className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                        {isDurationBasedFlex ? 'Eksik Süre Toleransı (dk)' : 'Çıkış Toleransı (dk)'}
                                    </label>
                                    <input
                                        type="number"
                                        name="exitToleranceMinutes"
                                        id="exitToleranceMinutes"
                                        value={formData.exitToleranceMinutes}
                                        onChange={handleChange}
                                        min="0"
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                    />
                                    <p className="mt-1 text-[11px] text-gray-500">
                                        {isDurationBasedFlex ? 'Hedef süreden eksik çalışma toleransı.' : 'Erken çıkış sayılmayacak süre.'}
                                    </p>
                                </div>
                                <div>
                                    <label htmlFor="maxOvertimeMinutes" className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                        Max. Günlük FM (dk)
                                    </label>
                                    <input
                                        type="number"
                                        name="maxOvertimeMinutes"
                                        id="maxOvertimeMinutes"
                                        value={formData.maxOvertimeMinutes}
                                        onChange={handleChange}
                                        min="0"
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                    />
                                    <p className="mt-1 text-[11px] text-gray-500">Günlük onaylanacak FM tavanı.</p>
                                </div>
                                <div>
                                    <label htmlFor="minValidWorkMinutes" className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                        Min. Geçerli Mesai (dk)
                                    </label>
                                    <input
                                        type="number"
                                        name="minValidWorkMinutes"
                                        id="minValidWorkMinutes"
                                        value={formData.minValidWorkMinutes}
                                        onChange={handleChange}
                                        min="0"
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold"
                                    />
                                    <p className="mt-1 text-[11px] text-gray-500">Altında kalırsa çalışma 0 sayılır.</p>
                                </div>
                            </div>

                            {/* Gerçek Süre Kredi Ayarları */}
                            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                                    Tolerans İçi Sapmaların Fiili Çalışma Süresine Etkisi
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {!isDurationBasedFlex && (
                                        <label className="flex items-start gap-2.5 cursor-pointer">
                                            <input
                                                id="tolerateLateEntryInActualDuration"
                                                name="tolerateLateEntryInActualDuration"
                                                type="checkbox"
                                                checked={formData.tolerateLateEntryInActualDuration}
                                                onChange={handleChange}
                                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <div className="text-xs">
                                                <div className="font-bold text-gray-900 dark:text-white">Tolerans İçi Geç Girişi Fiili Süreden Düşme</div>
                                                <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Giriş toleransı içindeki gecikmeleri (Örn: 5 dk tolerans varken 09:04 giriş) fiili çalışma süresine kredi olarak tamamlar.
                                                </p>
                                            </div>
                                        </label>
                                    )}
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            id="tolerateEarlyExitInActualDuration"
                                            name="tolerateEarlyExitInActualDuration"
                                            type="checkbox"
                                            checked={formData.tolerateEarlyExitInActualDuration}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <div className="text-xs">
                                            <div className="font-bold text-gray-900 dark:text-white">
                                                {isDurationBasedFlex ? 'Tolerans İçi Eksik Süreyi Tamamlanmış Say' : 'Tolerans İçi Erken Çıkışı Fiili Süreden Düşme'}
                                            </div>
                                            <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                {isDurationBasedFlex
                                                    ? 'Eksik çalışma süresi tolerans içinde kalırsa fiili çalışma süresini hedef süreye tamamlar.'
                                                    : 'Çıkış toleransı içindeki erken ayrılmaları fiili çalışma süresinden düşmez (tam mesai kabul eder).'}
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Fazla Mesai Tanımları */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {!isDurationBasedFlex && (
                                    <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                                        <label className="flex items-start gap-2.5 cursor-pointer">
                                            <input
                                                id="earlyOvertimeEnabled"
                                                name="earlyOvertimeEnabled"
                                                type="checkbox"
                                                checked={formData.earlyOvertimeEnabled}
                                                onChange={handleChange}
                                                disabled={formData.shiftType === 'FlexibleWindow' && formData.limitEarlyEntryToWindow}
                                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                            />
                                            <div className="text-xs">
                                                <div className="font-bold text-gray-900 dark:text-white">Erken Fazla Mesai (Vardiya Öncesi FM) Aktif</div>
                                                <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Vardiya başlangıcından önce yapılan çalışmalar eşiği aşarsa FM sayılır.
                                                </p>
                                                {formData.shiftType === 'FlexibleWindow' && formData.limitEarlyEntryToWindow && (
                                                    <p className="text-red-500 font-semibold mt-1">
                                                        * Erken giriş kırpma (Snap) açık olduğu için erken FM kapalıdır.
                                                    </p>
                                                )}
                                            </div>
                                        </label>
                                        {formData.earlyOvertimeEnabled && (
                                            <div>
                                                <label htmlFor="earlyOvertimeThresholdMinutes" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                                                    Erken Fazla Mesai Eşiği (dk)
                                                </label>
                                                <input
                                                    type="number"
                                                    name="earlyOvertimeThresholdMinutes"
                                                    id="earlyOvertimeThresholdMinutes"
                                                    value={formData.earlyOvertimeThresholdMinutes}
                                                    onChange={handleChange}
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            id="lateOvertimeEnabled"
                                            name="lateOvertimeEnabled"
                                            type="checkbox"
                                            checked={formData.lateOvertimeEnabled}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                        />
                                        <div className="text-xs">
                                            <div className="font-bold text-gray-900 dark:text-white">
                                                {isDurationBasedFlex ? 'Hedef Süre Aşımı Fazla Mesai Aktif' : 'Geç Fazla Mesai (Vardiya Sonrası FM) Aktif'}
                                            </div>
                                            <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                {isDurationBasedFlex
                                                    ? 'Zorunlu çalışma süresinin üzerindeki çalışmalar eşiği aşarsa FM sayılır.'
                                                    : 'Vardiya bitişinden sonra yapılan çalışmalar eşiği aşarsa FM sayılır.'}
                                            </p>
                                        </div>
                                    </label>
                                    {formData.lateOvertimeEnabled && (
                                        <div>
                                            <label htmlFor="lateOvertimeThresholdMinutes" className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                                                {isDurationBasedFlex ? 'Fazla Mesai Eşiği (dk)' : 'Geç Fazla Mesai Eşiği (dk)'}
                                            </label>
                                            <input
                                                type="number"
                                                name="lateOvertimeThresholdMinutes"
                                                id="lateOvertimeThresholdMinutes"
                                                value={formData.lateOvertimeThresholdMinutes}
                                                onChange={handleChange}
                                                min="0"
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Vardiya Yakalama (Catchment) Ayarları */}
                            <div className="p-4 rounded-xl bg-indigo-50/40 dark:bg-indigo-900/15 border border-indigo-200 dark:border-indigo-800/70">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 mb-3">
                                    Vardiya Turnike Yakalama Penceresi (Catchment Sapması)
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="shiftCatchmentStartHours" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            Vardiya Giriş Yakalama Sapması (Saat)
                                        </label>
                                        <input
                                            type="number"
                                            name="shiftCatchmentStartHours"
                                            id="shiftCatchmentStartHours"
                                            value={formData.shiftCatchmentStartHours}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.5"
                                            disabled={isDurationBasedFlex}
                                            className={`w-full px-3.5 py-2 border rounded-xl text-sm ${
                                                isDurationBasedFlex
                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed'
                                                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                            }`}
                                        />
                                        <p className="mt-1 text-[11px] text-gray-500">
                                            {isDurationBasedFlex
                                                ? 'Esnek vardiyada günün tamamı (00:00\'dan itibaren) yakalanır.'
                                                : 'Vardiya başlangıcından kaç saat önceki girişler bu vardiyaya bağlansın?'}
                                        </p>
                                    </div>
                                    <div>
                                        <label htmlFor="shiftCatchmentEndHours" className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                            Vardiya Çıkış Yakalama Sapması (Saat)
                                        </label>
                                        <input
                                            type="number"
                                            name="shiftCatchmentEndHours"
                                            id="shiftCatchmentEndHours"
                                            value={formData.shiftCatchmentEndHours}
                                            onChange={handleChange}
                                            min="0"
                                            step="0.5"
                                            className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm"
                                        />
                                        <p className="mt-1 text-[11px] text-gray-500">
                                            Vardiya bitişinden (veya 23:59'dan) kaç saat sonraki çıkışlar bu vardiyaya bağlansın?
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Alt Sabit Buton Barı */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex gap-2">
                            {activeSection !== 'general' && (
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(activeSection === 'rules' ? 'breaks' : 'general')}
                                    className="px-4 py-2 text-xs font-bold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200"
                                >
                                    ← Önceki Bölüm
                                </button>
                            )}
                            {activeSection !== 'rules' && (
                                <button
                                    type="button"
                                    onClick={() => setActiveSection(activeSection === 'general' ? 'breaks' : 'rules')}
                                    className="px-4 py-2 text-xs font-bold rounded-xl bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100"
                                >
                                    Sonraki Bölüm →
                                </button>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={submitting}
                                className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 font-medium text-sm"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center shadow-lg shadow-indigo-500/25"
                            >
                                {submitting && <Loader2 className="animate-spin mr-2" size={18} />}
                                {formData.id ? 'Vardiyayı Güncelle' : 'Vardiyayı Kaydet'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};


export default function WorkShiftManagement({ onDataChange }) {
    const { isAuthenticated, permissions } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [workShifts, setWorkShifts] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentShift, setCurrentShift] = useState(null);
    const [deleteConfirmData, setDeleteConfirmData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('ALL');

    const canView = useMemo(() => permissions.includes('scheduling:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('scheduling:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('scheduling:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('scheduling:delete'), [permissions]);

    const fetchWorkShifts = useCallback(async () => {
        if (!canView) {
            setError("Vardiyaları görüntüleme yetkiniz yok.");
            setLoading(false);
            return;
        }
        if (!isAuthenticated) return;
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('scheduling/shifts', { method: 'GET' });
            setWorkShifts(data || []);
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message || 'Vardiyalar yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, canView, onDataChange]);

    useEffect(() => {
        fetchWorkShifts();
    }, [fetchWorkShifts]);

    const handleOpenModal = (shift = null) => {
        const requiredPermission = shift ? 'scheduling:update' : 'scheduling:create';
        if (!permissions.includes(requiredPermission)) {
            alert("Bu işlemi yapma yetkiniz bulunmamaktadır.");
            return;
        }
        const shiftData = shift ? { ...shift, breakIds: (shift.breaks || []).map(b => b.id) } : null;
        setCurrentShift(shiftData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentShift(null);
        setError(null);
    };

    const handleSaveSuccess = () => {
        handleCloseModal();
        fetchWorkShifts();
    };

    const handleDeleteClick = (id) => {
        if (!canDelete) {
            setDeleteConfirmData({
                type: 'block',
                message: "Vardiya silme yetkiniz bulunmamaktadır."
            });
            return;
        }
        setDeleteConfirmData({
            id,
            type: 'initial',
            message: "Bu vardiyayı silmek istediğinizden emin misiniz?"
        });
    };

    const handleDeleteConfirm = async (id, force = false) => {
        try {
            await apiClient(`scheduling/shifts/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' });
            setDeleteConfirmData(null);
            fetchWorkShifts();
        } catch (err) {
            if (err.status === 409 && err.payload?.requiresConfirmation) {
                setDeleteConfirmData({
                    id,
                    type: 'conflict',
                    message: err.payload.message,
                    usedIn: err.payload.usedIn || []
                });
            } else {
                setDeleteConfirmData({
                    type: 'block',
                    message: err.message || "Silme işlemi sırasında bir hata oluştu."
                });
            }
        }
    };

    const filteredShifts = useMemo(() => {
        return workShifts.filter(s => {
            const type = mapShiftType(s.shiftType);
            const matchesSearch = !searchQuery
                || s.name?.toLowerCase().includes(searchQuery.toLowerCase())
                || s.shortCode?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesType = filterType === 'ALL' || type === filterType;
            return matchesSearch && matchesType;
        });
    }, [workShifts, searchQuery, filterType]);

    const stats = useMemo(() => ({
        total: workShifts.length,
        standard: workShifts.filter(s => mapShiftType(s.shiftType) === 'Standard').length,
        flexible: workShifts.filter(s => mapShiftType(s.shiftType) !== 'Standard').length,
        night: workShifts.filter(s => isNightShiftHelper(s)).length
    }), [workShifts]);

    const renderShiftTypeBadge = (shift) => {
        const type = mapShiftType(shift.shiftType);
        const calcMode = mapCalculationMethod(shift.calculationMethod);
        const modeLabel = calcMode === 'BasedOnTimeSpentInside' ? 'İçeride Kalınan Süre' : 'İlk Giriş - Son Çıkış';

        let badge;
        switch (type) {
            case 'FlexibleWindow':
                badge = <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300">Pencereli Esnek</span>;
                break;
            case 'FlexibleDaily':
                badge = <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">Günlük Esnek</span>;
                break;
            case 'FlexibleWeekly':
                badge = <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">Haftalık Esnek</span>;
                break;
            default:
                badge = <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">Sabit Vardiya</span>;
        }
        return (
            <div className="flex flex-col items-start gap-1">
                {badge}
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{modeLabel}</span>
            </div>
        );
    };

    const renderShiftScheduleInfo = (shift) => {
        const type = mapShiftType(shift.shiftType);
        const calcBrutFromHours = () => {
            if (!shift.startTime || !shift.endTime) return 480;
            const p1 = shift.startTime.split(':');
            const p2 = shift.endTime.split(':');
            let s = (parseInt(p1[0], 10) || 0) * 60 + (parseInt(p1[1], 10) || 0);
            let e = (parseInt(p2[0], 10) || 0) * 60 + (parseInt(p2[1], 10) || 0);
            if (e <= s) e += 24 * 60;
            return e - s;
        };

        if (type === 'FlexibleDaily') {
            const mins = (shift.requiredDurationMinutes && shift.requiredDurationMinutes <= 1440)
                ? shift.requiredDurationMinutes
                : calcBrutFromHours();
            return (
                <div>
                    <div className="font-bold text-emerald-700 dark:text-emerald-400 text-sm">
                        Günlük Hedef: {mins} dk <span className="text-xs text-gray-500">({(mins / 60).toFixed(1)} sa)</span>
                    </div>
                    {shift.startTime && shift.endTime && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            Ref: {shift.startTime.substring(0, 5)} - {shift.endTime.substring(0, 5)}
                        </div>
                    )}
                </div>
            );
        }
        if (type === 'FlexibleWeekly') {
            const mins = shift.requiredDurationMinutes || 2700;
            return (
                <div>
                    <div className="font-bold text-purple-700 dark:text-purple-400 text-sm">
                        Haftalık Hedef: {(mins / 60).toFixed(1)} saat <span className="text-xs text-gray-500">({mins} dk)</span>
                    </div>
                    {shift.startTime && shift.endTime && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            Günlük Ref: {shift.startTime.substring(0, 5)} - {shift.endTime.substring(0, 5)}
                        </div>
                    )}
                </div>
            );
        }
        if (type === 'FlexibleWindow') {
            return (
                <div>
                    <div className="font-bold text-gray-900 dark:text-white font-mono text-sm">
                        {shift.startTime?.substring(0, 5)} - {shift.endTime?.substring(0, 5)}
                    </div>
                    {shift.flexibleEntryStartTime && shift.flexibleEntryEndTime && (
                        <div className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                            Esnek Giriş: {shift.flexibleEntryStartTime.substring(0, 5)} - {shift.flexibleEntryEndTime.substring(0, 5)}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <div className="font-bold text-gray-900 dark:text-white font-mono text-sm">
                {shift.startTime?.substring(0, 5)} - {shift.endTime?.substring(0, 5)}
            </div>
        );
    };

    const renderRuleSummaryBadges = (shift) => {
        const type = mapShiftType(shift.shiftType);
        const isFlexDuration = type === 'FlexibleDaily' || type === 'FlexibleWeekly';
        return (
            <div className="flex flex-wrap justify-end gap-1.5">
                {!isFlexDuration ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        Tol: {shift.entryToleranceMinutes}dk / {shift.exitToleranceMinutes}dk
                    </span>
                ) : (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        Eksik Tol: {shift.exitToleranceMinutes}dk
                    </span>
                )}
                {shift.lateOvertimeEnabled && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
                        FM &gt;{shift.lateOvertimeThresholdMinutes}dk
                    </span>
                )}
                {shift.earlyOvertimeEnabled && !isFlexDuration && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800">
                        Erken FM &gt;{shift.earlyOvertimeThresholdMinutes}dk
                    </span>
                )}
            </div>
        );
    };

    if (!loading && !canView) {
        return (
            <div className="p-8 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-bold text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Toplam Vardiya</p>
                        <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Briefcase size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sabit Vardiyalar</p>
                        <p className="text-2xl font-extrabold text-blue-600 dark:text-blue-400 mt-1">{stats.standard}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <Clock size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Esnek Vardiyalar</p>
                        <p className="text-2xl font-extrabold text-purple-600 dark:text-purple-400 mt-1">{stats.flexible}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                        <Sparkles size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Gece Vardiyaları</p>
                        <p className="text-2xl font-extrabold text-amber-600 dark:text-amber-400 mt-1">{stats.night}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <Moon size={22} />
                    </div>
                </div>
            </div>

            {/* Ana Tablo Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Briefcase size={20} className="text-indigo-500" />
                            Vardiya ve Mesai Kuralları
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Sabit, pencereli esnek, günlük esnek ve haftalık esnek vardiya şablonlarını yönetin.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Vardiya adı veya kod ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-3 py-2 text-xs font-semibold border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                        >
                            <option value="ALL">Tüm Vardiya Türleri</option>
                            <option value="Standard">Sabit Vardiya</option>
                            <option value="FlexibleWindow">Pencereli Esnek</option>
                            <option value="FlexibleDaily">Günlük Esnek</option>
                            <option value="FlexibleWeekly">Haftalık Esnek</option>
                        </select>

                        {canCreate && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl flex items-center shadow-sm shadow-indigo-500/20 transition-colors"
                            >
                                <PlusCircle size={17} className="mr-1.5" />
                                Yeni Vardiya Ekle
                            </button>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="p-12 flex justify-center items-center text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={20} /> Vardiyalar yükleniyor...
                    </div>
                )}

                {error && (
                    <div className="m-5 text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl flex items-center text-sm">
                        <AlertCircle size={18} className="mr-2" /> {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50/80 dark:bg-gray-900/50">
                                <tr>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vardiya Adı & Kod</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tür & Hesaplama Modu</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Saat Aralığı / Hedef</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bağlı Molalar</th>
                                    <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tolerans & FM Özeti</th>
                                    {(canUpdate || canDelete) && <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/70">
                                {filteredShifts.map(shift => {
                                    const isNight = isNightShiftHelper(shift);
                                    return (
                                        <tr key={shift.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${isNight ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'}`}>
                                                        {isNight ? <Moon size={17} /> : <Briefcase size={17} />}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                            {shift.name}
                                                            {shift.shortCode && (
                                                                <span className="px-2 py-0.5 text-[11px] font-mono font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                    {shift.shortCode}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isNight && (
                                                            <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                                                                Gece / Gün Aşan Vardiya
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renderShiftTypeBadge(shift)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {renderShiftScheduleInfo(shift)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {(shift.breaks || []).map(b => (
                                                        <span key={b.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">
                                                            <Coffee size={11} /> {b.name}
                                                        </span>
                                                    ))}
                                                    {(!shift.breaks || shift.breaks.length === 0) && (
                                                        <span className="text-xs text-gray-400 italic">Mola yok</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                {renderRuleSummaryBadges(shift)}
                                            </td>
                                            {(canUpdate || canDelete) && (
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="inline-flex items-center gap-1.5">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() => handleOpenModal(shift)}
                                                                className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30 transition-colors"
                                                                title="Düzenle"
                                                            >
                                                                <Edit size={17} />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() => handleDeleteClick(shift.id)}
                                                                className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                                                title="Sil"
                                                            >
                                                                <Trash2 size={17} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredShifts.length === 0 && (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                                Kriterlere uygun vardiya tanımı bulunamadı.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && <WorkShiftForm currentShift={currentShift} onSave={handleSaveSuccess} onCancel={handleCloseModal} permissions={permissions} />}

            {/* Dinamik Silme Modalı */}
            {deleteConfirmData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start mb-4">
                            <div className={`flex-shrink-0 p-3 rounded-full mr-4 ${
                                deleteConfirmData.type === 'block' ? 'bg-red-100 dark:bg-red-900/30' :
                                deleteConfirmData.type === 'conflict' ? 'bg-amber-100 dark:bg-amber-900/30' :
                                'bg-red-100 dark:bg-red-900/30'
                            }`}>
                                {deleteConfirmData.type === 'block' ? (
                                    <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
                                ) : deleteConfirmData.type === 'conflict' ? (
                                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                ) : (
                                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                    {deleteConfirmData.type === 'block' ? 'İşlem Engellendi' :
                                     deleteConfirmData.type === 'conflict' ? 'Uyarı: Bağlı Kayıtlar Bulundu' :
                                     'Silme Onayı'}
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {deleteConfirmData.message}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setDeleteConfirmData(null)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
                            >
                                {deleteConfirmData.type === 'block' ? 'Kapat' : 'İptal'}
                            </button>
                            {deleteConfirmData.type !== 'block' && (
                                <button
                                    onClick={() => handleDeleteConfirm(deleteConfirmData.id, deleteConfirmData.type === 'conflict')}
                                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm flex items-center shadow-lg shadow-red-500/30"
                                >
                                    {deleteConfirmData.type === 'conflict' ? 'Onaylıyorum, Zorla Sil' : 'Evet, Sil'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
